import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MatterCombobox, ClientCombobox } from '@/components/common/EntityCombobox';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { UnifiedInvoice, CREDIT_REASONS, VAT_TYPES, InvoiceOverrides, INVOICE_DETAIL_LEVELS, DISCOUNT_TYPES } from '@/types/invoicing';
import type { AdjustmentLine } from '@/types/invoicing';
import type { FeeSharingConfig } from '@/types/fee-sharing';
import { AdjustmentLinesEditor } from './AdjustmentLinesEditor';
import { formatCurrency } from '@/utils/document';

interface CreateInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  createType: 'invoice' | 'credit_note' | 'downpayment';
  matters: Array<{ id: number; name: string; group_name?: string; status?: string; fee_sharing_configs?: FeeSharingConfig[] }>;
  clients: Array<{ id: number; name: string }>;
  invoices: UnifiedInvoice[];
  onInvoiceCreated: () => void;
  defaultInvoiceDescription?: string;
}

interface CreateFormData {
  matter_id: string;
  client_id: string;
  invoice_number: string;
  amount: string;
  description: string;
  issue_date: string;
  due_days: string;
  credit_reason: string;
  original_invoice_id: string;
  send_email_notification: boolean;
  deduct_from_next_invoice: boolean;
  // Override fields
  override_subtotal: string;
  override_service_charge: string;
  override_vat_type: string;
  override_vat_rate: string;
  override_vat_amount: string;
  override_total: string;
  override_fee_split_percentage: string;
  override_fee_split_amount: string;
  override_due_date: string;
  // Adjustment fields
  discount_type: 'none' | 'percentage' | 'fixed' | 'both';
  discount_percentage: string;
  discount_amount: string;
  discount_description: string;
  show_discount_on_invoice: boolean;
  flat_fee_adjustment: string;
  flat_fee_description: string;
  show_flat_fee_on_invoice: boolean;
  target_amount: string;
  fixed_deduction_amount: string;
  fixed_deduction_description: string;
  show_fixed_deduction_on_invoice: boolean;
  detail_level: '' | 'short' | 'summary' | 'detailed';
}

const initialFormData: CreateFormData = {
  matter_id: '',
  client_id: '',
  invoice_number: '',
  amount: '',
  description: '',
  issue_date: format(new Date(), 'yyyy-MM-dd'),
  due_days: '30',
  credit_reason: '',
  original_invoice_id: '',
  send_email_notification: false,
  deduct_from_next_invoice: false,
  override_subtotal: '',
  override_service_charge: '',
  override_vat_type: '',
  override_vat_rate: '',
  override_vat_amount: '',
  override_total: '',
  override_fee_split_percentage: '',
  override_fee_split_amount: '',
  override_due_date: '',
  discount_type: 'none',
  discount_percentage: '',
  discount_amount: '',
  discount_description: '',
  show_discount_on_invoice: true,
  flat_fee_adjustment: '',
  flat_fee_description: '',
  show_flat_fee_on_invoice: true,
  target_amount: '',
  fixed_deduction_amount: '',
  fixed_deduction_description: '',
  show_fixed_deduction_on_invoice: true,
  detail_level: '',
};

export function CreateInvoiceDialog({ 
  isOpen, 
  onClose, 
  createType, 
  matters,
  clients,
  invoices, 
  onInvoiceCreated,
  defaultInvoiceDescription,
}: CreateInvoiceDialogProps) {
  const [formData, setFormData] = useState<CreateFormData>(initialFormData);
  const [isCreating, setIsCreating] = useState(false);
  const [adjustmentLines, setAdjustmentLines] = useState<AdjustmentLine[]>([]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        ...initialFormData,
        description: createType === 'invoice' ? (defaultInvoiceDescription || '') : '',
      });
      setAdjustmentLines([]);
    }
  }, [isOpen, createType, defaultInvoiceDescription]);

  // Filter invoices by selected matter for credit note linking
  const matterInvoices = useMemo(() => {
    if (!formData.matter_id) return [];
    return invoices.filter(
      inv => inv.invoice_type === 'invoice' && inv.matter === parseInt(formData.matter_id)
    );
  }, [invoices, formData.matter_id]);

  // Get client IDs from selected matter's fee-sharing config
  const matterClientIds = useMemo(() => {
    if (!formData.matter_id) return null; // null = no filter, show all
    const matter = matters.find(m => m.id === parseInt(formData.matter_id));
    if (!matter?.fee_sharing_configs?.length) return null;
    const activeConfig = matter.fee_sharing_configs.find(c => c.is_active) || matter.fee_sharing_configs[0];
    const ids = new Set(activeConfig?.participants?.map(p => p.client) || []);
    return ids.size > 0 ? ids : null;
  }, [formData.matter_id, matters]);

  // Filter clients: when a matter is selected, show only that matter's clients
  const filteredClients = useMemo(() => {
    const base = matterClientIds
      ? clients.filter(c => matterClientIds.has(c.id))
      : clients;
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, matterClientIds]);

  // Reset client selection when matter changes and client is no longer in filtered list
  useEffect(() => {
    if (formData.client_id && matterClientIds && !matterClientIds.has(parseInt(formData.client_id))) {
      setFormData(prev => ({ ...prev, client_id: '' }));
    }
  }, [matterClientIds, formData.client_id]);

  // Build overrides object from form data
  const buildOverrides = (): InvoiceOverrides | undefined => {
    const overrides: InvoiceOverrides = {};
    let hasOverrides = false;

    const fsp = Number(formData.override_fee_split_percentage) || 0;

    if (formData.override_subtotal) { overrides.subtotal = formData.override_subtotal; hasOverrides = true; }
    if (formData.override_service_charge) {
      // Service charge is sent as-is; the backend computes per-participant amounts
      overrides.service_charge = formData.override_service_charge;
      hasOverrides = true;
    }
    if (formData.override_vat_type) { overrides.vat_type = formData.override_vat_type as any; hasOverrides = true; }
    if (formData.override_vat_rate) { overrides.vat_rate = formData.override_vat_rate; hasOverrides = true; }
    if (formData.override_vat_amount) { overrides.vat_amount = formData.override_vat_amount; hasOverrides = true; }
    if (formData.override_fee_split_percentage) { overrides.fee_split_percentage = fsp; hasOverrides = true; }
    if (formData.override_fee_split_amount) { overrides.fee_split_amount = formData.override_fee_split_amount; hasOverrides = true; }
    // Auto-calculate total: use explicit override if set, otherwise compute from fee split (inverse of percentage)
    if (formData.override_total) {
      overrides.total = formData.override_total;
      hasOverrides = true;
    } else if (fsp > 0 && formData.override_fee_split_amount) {
      const fsa = Number(formData.override_fee_split_amount) || 0;
      if (fsa > 0) {
        overrides.total = String((fsa / fsp * 100).toFixed(2));
        hasOverrides = true;
      }
    }
    if (formData.override_due_date) { overrides.due_date = formData.override_due_date; hasOverrides = true; }

    return hasOverrides ? overrides : undefined;
  };

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const overrides = buildOverrides();
      let requestData: any = {
        matter_id: parseInt(formData.matter_id),
        invoice_type: createType,
        issue_date: formData.issue_date,
        send_email_notification: formData.send_email_notification,
      };

      if (formData.client_id) {
        requestData.client_id = parseInt(formData.client_id);
      }
      if (formData.invoice_number.trim()) {
        requestData.invoice_number = formData.invoice_number.trim();
      }
      if (overrides) {
        requestData.overrides = overrides;
      }

      // Add adjustment fields
      if (formData.discount_type !== 'none') {
        requestData.discount_type = formData.discount_type;
        if ((formData.discount_type === 'percentage' || formData.discount_type === 'both') && formData.discount_percentage) {
          requestData.discount_percentage = formData.discount_percentage;
        }
        if ((formData.discount_type === 'fixed' || formData.discount_type === 'both') && formData.discount_amount) {
          requestData.discount_amount = formData.discount_amount;
        }
        if (formData.discount_description) requestData.discount_description = formData.discount_description;
        requestData.show_discount_on_invoice = formData.show_discount_on_invoice;
      }
      if (formData.flat_fee_adjustment && formData.flat_fee_adjustment !== '0') {
        requestData.flat_fee_adjustment = formData.flat_fee_adjustment;
        if (formData.flat_fee_description) requestData.flat_fee_description = formData.flat_fee_description;
        requestData.show_flat_fee_on_invoice = formData.show_flat_fee_on_invoice;
      }
      if (formData.target_amount && formData.target_amount !== '0') {
        requestData.target_amount = formData.target_amount;
      }
      if (formData.fixed_deduction_amount && formData.fixed_deduction_amount !== '0') {
        requestData.fixed_deduction_amount = formData.fixed_deduction_amount;
        if (formData.fixed_deduction_description) requestData.fixed_deduction_description = formData.fixed_deduction_description;
        requestData.show_fixed_deduction_on_invoice = formData.show_fixed_deduction_on_invoice;
      }
      if (formData.detail_level) {
        requestData.detail_level = formData.detail_level;
      }

      // Structured adjustment lines
      if (adjustmentLines.length > 0) {
        requestData.adjustment_lines = adjustmentLines.map((line, i) => ({
          line_type: line.line_type,
          description: line.description,
          amount: parseFloat(String(line.amount)) || 0,
          percentage: line.percentage ? parseFloat(String(line.percentage)) : null,
          show_on_invoice: line.show_on_invoice !== false,
          order: i,
        }));
      }

      if (createType === 'invoice') {
        requestData = {
          ...requestData,
          amount: formData.amount,
          description: formData.description,
          due_days: parseInt(formData.due_days) || 30,
          deduct_from_next_invoice: formData.deduct_from_next_invoice,
        };
      } else if (createType === 'credit_note') {
        requestData = {
          ...requestData,
          amount: formData.amount,
          description: formData.description,
          credit_reason: formData.credit_reason,
        };
        if (formData.original_invoice_id) {
          requestData.original_invoice_id = parseInt(formData.original_invoice_id);
        }
      } else if (createType === 'downpayment') {
        requestData = {
          ...requestData,
          amount: formData.amount,
          description: formData.description,
          due_days: parseInt(formData.due_days) || 30,
        };
      }

      await UnifiedInvoiceAPI.createInvoice(requestData);

      toast({
        title: 'Success',
        description: `${createType === 'invoice' ? 'Invoice' : createType === 'credit_note' ? 'Credit note' : 'Down payment'} created successfully`,
      });

      onInvoiceCreated();
      handleClose();
    } catch (error: any) {
      console.error('Failed to create:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || `Failed to create ${createType.replace('_', ' ')}`;
      toast({
        title: 'Error',
        description: typeof errorMessage === 'string' ? errorMessage : `Failed to create ${createType.replace('_', ' ')}`,
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  const isFormValid = () => {
    const hasRequiredFields = formData.matter_id && formData.amount;
    const hasCreditReason = createType !== 'credit_note' || formData.credit_reason;
    return hasRequiredFields && hasCreditReason;
  };

  const getDialogTitle = () => {
    switch (createType) {
      case 'invoice': return 'Create Standalone Invoice';
      case 'credit_note': return 'Create Credit Note';
      case 'downpayment': return 'Create Down Payment';
      default: return 'Create Document';
    }
  };

  const getDialogDescription = () => {
    switch (createType) {
      case 'invoice': return 'Create a fixed-amount invoice. Optionally deduct from the next timesheet-based invoice.';
      case 'credit_note': return 'Create a credit note to reduce a client\'s balance. Optionally link to a specific invoice.';
      case 'downpayment': return 'Create a down payment that can be applied against future invoices.';
      default: return '';
    }
  };

  const getButtonText = () => {
    switch (createType) {
      case 'invoice': return 'Create Invoice';
      case 'credit_note': return 'Create Credit Note';
      case 'downpayment': return 'Create Down Payment';
      default: return 'Create';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Matter selection */}
          <div>
            <Label htmlFor="matter">Matter *</Label>
            <MatterCombobox
              matters={matters}
              value={formData.matter_id ? Number(formData.matter_id) : null}
              onChange={(id) => setFormData(prev => ({ ...prev, matter_id: id ? String(id) : '', original_invoice_id: '' }))}
            />
          </div>

          {/* Client override (optional) */}
          <div>
            <Label htmlFor="client">
              Client <span className="text-xs text-muted-foreground">(optional — overrides fee-sharing auto-selection)</span>
            </Label>
            <ClientCombobox
              clients={filteredClients}
              value={formData.client_id ? Number(formData.client_id) : null}
              onChange={(id) => setFormData(prev => ({ ...prev, client_id: id ? String(id) : '' }))}
              noneLabel="Auto (from matter/fee-sharing)"
            />
            {matterClientIds ? (
              <p className="text-xs text-muted-foreground mt-1">
                Showing {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} from selected matter's fee-sharing config.
              </p>
            ) : formData.matter_id ? (
              <p className="text-xs text-muted-foreground mt-1">
                This matter has no fee-sharing configuration — the matter's own client will be billed unless you pick one here.
              </p>
            ) : null}
          </div>

          {/* Invoice Number (optional) */}
          <div>
            <Label htmlFor="invoice_number">
              Invoice Number <span className="text-xs text-muted-foreground">(optional — auto-generated if empty)</span>
            </Label>
            <Input
              id="invoice_number"
              value={formData.invoice_number}
              onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
              placeholder="Auto-generated"
            />
          </div>

          {/* Amount */}
          <div>
            <Label htmlFor="amount">Amount (before VAT) *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description... (supports template variables like {{ begin_date }})"
              rows={3}
            />
            {createType === 'invoice' && defaultInvoiceDescription && formData.description === defaultInvoiceDescription && (
              <p className="text-xs text-muted-foreground mt-1">Using firm default. Edit or clear to override.</p>
            )}
          </div>

          {/* Date and Due Days */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="issue_date">Issue Date</Label>
              <DatePicker
                id="issue_date"
                value={formData.issue_date}
                onChange={(iso) => setFormData(prev => ({ ...prev, issue_date: iso }))}
              />
            </div>
            {(createType === 'invoice' || createType === 'downpayment') && (
              <div>
                <Label htmlFor="due_days">Due Days</Label>
                <Input
                  id="due_days"
                  type="number"
                  value={formData.due_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_days: e.target.value }))}
                  placeholder="30"
                />
              </div>
            )}
          </div>

          {/* Credit Note: reason and original invoice */}
          {createType === 'credit_note' && (
            <>
              <div>
                <Label htmlFor="credit_reason">Credit Reason *</Label>
                <Select
                  value={formData.credit_reason}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, credit_reason: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDIT_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="original_invoice">Link to Invoice (optional)</Label>
                <Select
                  value={formData.original_invoice_id || "none"}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    original_invoice_id: value === "none" ? "" : value 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None (matter-level credit)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (matter-level credit note)</SelectItem>
                    {matterInvoices.map((invoice) => (
                      <SelectItem key={invoice.id} value={invoice.id.toString()}>
                        {invoice.invoice_number} — {formatCurrency(invoice.total)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.original_invoice_id 
                    ? 'Credit note linked to specific invoice. Amount validated against original.'
                    : 'No link: creates one credit note per fee-sharing participant, split proportionally.'}
                </p>
              </div>
            </>
          )}

          {/* Standalone Invoice: deduction toggle */}
          {createType === 'invoice' && (
            <div className="rounded-lg border p-3 space-y-1">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="deduct_next"
                  checked={formData.deduct_from_next_invoice}
                  onChange={(e) => setFormData(prev => ({ ...prev, deduct_from_next_invoice: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="deduct_next" className="font-medium">Deduct from next timesheet invoice</Label>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                When enabled, this amount will be automatically deducted from the next timesheet-based invoice for the same matter and client.
              </p>
            </div>
          )}

          {/* Email notification */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="send_email"
              checked={formData.send_email_notification}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                send_email_notification: e.target.checked 
              }))}
              className="rounded"
            />
            <Label htmlFor="send_email">Send email notification</Label>
          </div>

          {/* Advanced: Overrides */}
          <Accordion type="single" collapsible>
            <AccordionItem value="overrides" className="border rounded-lg px-3">
              <AccordionTrigger className="text-sm py-2">
                Advanced: Override calculated values
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-3">
                <p className="text-xs text-muted-foreground">
                  Override any auto-calculated values. Leave empty to use defaults.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Subtotal</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto"
                      value={formData.override_subtotal}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_subtotal: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Service Charge</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto"
                      value={formData.override_service_charge}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_service_charge: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">VAT Type</Label>
                    <Select
                      value={formData.override_vat_type || "auto"}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, override_vat_type: v === "auto" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Auto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto</SelectItem>
                        {VAT_TYPES.map(vt => (
                          <SelectItem key={vt.value} value={vt.value}>{vt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">VAT Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto"
                      value={formData.override_vat_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_vat_rate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">VAT Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto"
                      value={formData.override_vat_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_vat_amount: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Total</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto"
                      value={formData.override_total}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_total: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Fee Split %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="Auto"
                      value={formData.override_fee_split_percentage}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_fee_split_percentage: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Fee Split Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Auto"
                      value={formData.override_fee_split_amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, override_fee_split_amount: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Override Due Date</Label>
                  <DatePicker
                    value={formData.override_due_date}
                    onChange={(iso) => setFormData(prev => ({ ...prev, override_due_date: iso }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty to auto-calculate from issue date + due days</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Invoice Adjustments */}
          <Accordion type="single" collapsible>
            <AccordionItem value="adjustments" className="border rounded-lg px-3">
              <AccordionTrigger className="text-sm py-2">
                Invoice Adjustments (Discount, Flat Fee, Deductions)
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pb-3">
                {/* Detail Level */}
                <div>
                  <Label className="text-xs">Invoice Detail Level</Label>
                  <Select
                    value={formData.detail_level || 'default'}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, detail_level: v === 'default' ? '' : v as any }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Use firm default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Use firm default</SelectItem>
                      {INVOICE_DETAIL_LEVELS.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Discount */}
                <div className="space-y-2 border-t pt-3">
                  <Label className="text-xs font-medium">Discount</Label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, discount_type: v as any }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_TYPES.map(dt => (
                        <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(formData.discount_type === 'percentage' || formData.discount_type === 'both') && (
                    <div>
                      <Label className="text-xs">Percentage (%)</Label>
                      <Input
                        type="number" min="0" max="100" step="0.01"
                        placeholder="e.g. 10"
                        value={formData.discount_percentage}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.target.value }))}
                      />
                    </div>
                  )}
                  {(formData.discount_type === 'fixed' || formData.discount_type === 'both') && (
                    <div>
                      <Label className="text-xs">Amount (€)</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        placeholder="e.g. 500"
                        value={formData.discount_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_amount: e.target.value }))}
                      />
                    </div>
                  )}
                  {formData.discount_type !== 'none' && (
                    <>
                      <Input
                        placeholder="Discount description (optional)"
                        value={formData.discount_description}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_description: e.target.value }))}
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={formData.show_discount_on_invoice}
                          onCheckedChange={(v) => setFormData(prev => ({ ...prev, show_discount_on_invoice: v }))}
                        />
                        <Label className="text-xs">Show on invoice</Label>
                      </div>
                    </>
                  )}
                </div>

                {/* Flat Fee */}
                <div className="space-y-2 border-t pt-3">
                  <Label className="text-xs font-medium">Flat Fee Adjustment</Label>
                  <Input
                    type="number" step="0.01"
                    placeholder="Amount (€) — positive adds, negative subtracts"
                    value={formData.flat_fee_adjustment}
                    onChange={(e) => setFormData(prev => ({ ...prev, flat_fee_adjustment: e.target.value }))}
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={formData.flat_fee_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, flat_fee_description: e.target.value }))}
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.show_flat_fee_on_invoice}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, show_flat_fee_on_invoice: v }))}
                    />
                    <Label className="text-xs">Show on invoice</Label>
                  </div>
                </div>

                {/* Target Amount */}
                <div className="space-y-2 border-t pt-3">
                  <Label className="text-xs font-medium">Target Amount</Label>
                  <p className="text-xs text-muted-foreground">Set a desired total and the system auto-calculates a flat fee adjustment.</p>
                  <Input
                    type="number" min="0" step="0.01"
                    placeholder="Desired total (€ excl. VAT)"
                    value={formData.target_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_amount: e.target.value }))}
                  />
                </div>

                {/* Fixed Deduction */}
                <div className="space-y-2 border-t pt-3">
                  <Label className="text-xs font-medium">Fixed Deduction</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    placeholder="Amount (€)"
                    value={formData.fixed_deduction_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, fixed_deduction_amount: e.target.value }))}
                  />
                  <Input
                    placeholder="Description (e.g. Pre-paid retainer)"
                    value={formData.fixed_deduction_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, fixed_deduction_description: e.target.value }))}
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.show_fixed_deduction_on_invoice}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, show_fixed_deduction_on_invoice: v }))}
                    />
                    <Label className="text-xs">Show on invoice</Label>
                  </div>
                </div>

                {/* Additional adjustment lines (multiple discounts/deductions) */}
                <div className="border-t pt-3">
                  <AdjustmentLinesEditor
                    lines={adjustmentLines}
                    onChange={setAdjustmentLines}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={isCreating || !isFormValid()}
          >
            {isCreating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" />
                Creating...
              </>
            ) : (
              getButtonText()
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
